import {
    WIDGET_ARROW_KEY_DIRECTIONS,
    WIDGET_INSET_PX,
    WIDGET_KEY_STEP_LARGE_PX,
    WIDGET_KEY_STEP_PX,
} from '~/viewportGrid';

/** A panel's top-left corner in window pixels. */
export interface PanelPosition {
    x: number;
    y: number;
}

/** Gap between the anchor's edge and the panel that opens beside it. */
const ANCHOR_GAP_PX = 26;
/** The least distance from the window's sides and bottom where a panel opens. */
const WINDOW_MARGIN_PX = 16;
/** The least distance from the window's top edge where a panel opens. */
const TOP_MARGIN_PX = 50;

/**
 * Where a floating panel of `panelWidth` opens beside `anchor`, top-aligned
 * with it: to its right, or to its left when the right would fall past the
 * window edge.
 */
export function panelPositionBeside(
    anchor: DOMRect,
    panelWidth: number
): PanelPosition {
    const preferredX = anchor.right + ANCHOR_GAP_PX;
    const maxX = window.innerWidth - panelWidth - WINDOW_MARGIN_PX;
    return {
        x:
            preferredX > maxX
                ? Math.max(
                      WINDOW_MARGIN_PX,
                      anchor.left - panelWidth - ANCHOR_GAP_PX
                  )
                : preferredX,
        y: Math.max(TOP_MARGIN_PX, anchor.top),
    };
}

/**
 * Where a floating panel of `panelWidth` opens when nothing anchors it: the
 * window's top-right corner, inside the margins.
 */
export function panelPositionAtTopRight(panelWidth: number): PanelPosition {
    return {
        x: Math.max(
            WINDOW_MARGIN_PX,
            window.innerWidth - panelWidth - WINDOW_MARGIN_PX
        ),
        y: TOP_MARGIN_PX,
    };
}

/**
 * Where a floating panel of `panelWidth` opens: beside `anchor`, or at the
 * window's top-right corner without one.
 */
export function panelPositionOpening(
    anchor: Element | null,
    panelWidth: number
): PanelPosition {
    return anchor
        ? panelPositionBeside(anchor.getBoundingClientRect(), panelWidth)
        : panelPositionAtTopRight(panelWidth);
}

/** Least gap between a panel the user moves and the window's edges, in CSS pixels. */
export const PANEL_WINDOW_INSET_PX = WIDGET_INSET_PX;

/** The size of an area or a panel, in CSS pixels. */
export interface Size {
    width: number;
    height: number;
}

/**
 * Keeps a panel of `panel` size inside `area`, such as the window,
 * {@link PANEL_WINDOW_INSET_PX} from every edge where the area is large
 * enough, and against the top left where it is not. All values are in CSS
 * pixels, the position from the area's top left.
 */
export function clampedPanelPosition(
    position: PanelPosition,
    area: Size,
    panel: Size
): PanelPosition {
    const clamp = (value: number, room: number) =>
        Math.max(
            PANEL_WINDOW_INSET_PX,
            Math.min(value, room - PANEL_WINDOW_INSET_PX)
        );
    return {
        x: clamp(position.x, area.width - panel.width),
        y: clamp(position.y, area.height - panel.height),
    };
}

/**
 * Where an arrow key moves a panel from `position`, kept inside `area` as a
 * drag keeps it, or null for any other key. `large` takes the larger step.
 * All values are in CSS pixels, the position from the area's top left.
 */
export function panelPositionAfterKey(
    key: string,
    large: boolean,
    position: PanelPosition,
    area: Size,
    panel: Size
): PanelPosition | null {
    const direction = WIDGET_ARROW_KEY_DIRECTIONS[key];
    if (!direction) {
        return null;
    }
    const step = large ? WIDGET_KEY_STEP_LARGE_PX : WIDGET_KEY_STEP_PX;
    return clampedPanelPosition(
        {
            x: position.x + direction[0] * step,
            y: position.y + direction[1] * step,
        },
        area,
        panel
    );
}
